// §18.1 component inventory: "FormSheet — modal on desktop, full page on mobile; primary
// action right, always Cmd/Ctrl+Enter submits; Zod inline errors under fields; dirty-close
// asks." Dirty-close confirmation is not yet implemented (no form here is dirty-tracked
// yet) — deferred until a form needs it.
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { ghostButtonInlineClass, primaryButtonInlineClass } from './FormKit';

export function FormSheet({
  open,
  onOpenChange,
  title,
  children,
  onSubmit,
  submitLabel = 'Save',
  submitting = false,
  submitDisabled = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  onSubmit: () => void;
  submitLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
}): React.JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed inset-0 z-50 flex flex-col bg-surface outline-none sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[85vh] sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[var(--radius-card)] sm:border sm:border-border"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !submitDisabled) {
              e.preventDefault();
              onSubmit();
            }
          }}
        >
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
            <Dialog.Title className="font-semibold text-ink">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="rounded-[var(--radius-control)] p-1.5 text-muted hover:bg-bg hover:text-ink"
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">{title}</Dialog.Description>
          <div className="flex-1 overflow-y-auto p-4">{children}</div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-border p-4">
            <Dialog.Close asChild>
              <button type="button" className={ghostButtonInlineClass}>
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              className={primaryButtonInlineClass}
              onClick={onSubmit}
              disabled={submitting || submitDisabled}
            >
              {submitLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
